import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type CleaningTeam, type PropertyCleaningSettings } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'

interface CleaningCardProps {
  propertyId: string
  isActive: boolean
  /** True once `managed_until` has passed: the property is offboarded. */
  archived?: boolean
  managedUntil?: string | null
}

interface CleaningResponse {
  settings: PropertyCleaningSettings
  teams: CleaningTeam[]
}

/**
 * Per-property cleaning configuration: whether the property gets cleaning jobs
 * at all, which crew is assigned by default, and what each crew charges here.
 *
 * Two things this card is deliberately loud about, because both are silent
 * failures on the backend side:
 *
 * 1. **Enabled with no default crew.** Auto-assignment is the property default
 *    and nothing else, so an enabled property without one derives jobs assigned
 *    to nobody — and that looks identical to a job simply not assigned yet.
 * 2. **A default crew with no rate.** A missing rate resolves to null, never 0,
 *    so the payable has no amount. An empty rate box and a rate of $0 must
 *    never look the same, which is why blank is rendered as "not set" rather
 *    than as a zero.
 *
 * The global check-in/check-out times and the crews themselves are edited once,
 * globally, on Settings - not here.
 *
 * **An offboarded property still renders this card, read-only** (CAP-382). The
 * rates stay visible on purpose: they are what a historical payable's amount
 * was calculated from, so hiding them would leave a past payment unexplainable.
 * Editing is closed because there is nothing left to configure - admin-api
 * derives no new jobs for a property we no longer manage - and an editable form
 * that silently changes nothing operational is worse than no form.
 */
export function CleaningCard({
  propertyId,
  isActive,
  archived = false,
  managedUntil,
}: CleaningCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)
  const [enabled, setEnabled] = useState(true)
  const [defaultTeamId, setDefaultTeamId] = useState<string>('')
  /** Rate inputs as typed strings — '' means "no rate", which is not 0. */
  const [rates, setRates] = useState<Record<string, string>>({})

  const { data, isLoading, error, refetch } = useQuery<CleaningResponse>({
    queryKey: ['property-cleaning', propertyId],
    queryFn: () => api.getPropertyCleaning(propertyId),
    staleTime: 30000,
    enabled: !!propertyId && !!isActive,
  })

  /**
   * Re-seeded whenever fresh data arrives, unless the operator has touched the
   * form.
   *
   * Seeding only ONCE per property is the obvious version and is wrong: react-query
   * serves the cached value on mount and refetches behind it, so a card that
   * ignores every response after the first shows the stale value, and saving
   * then writes it back over whatever the newer one was. Tracking dirtiness
   * instead keeps an in-progress edit safe without letting the screen lie about
   * what the backend holds — which is the rule admin-api's mental model states
   * outright: state shown in the UI comes from an API response.
   */
  const isDirty = useRef(false)
  const seededFor = useRef<string | null>(null)
  useEffect(() => {
    if (!data) return
    // Dirtiness is PER PROPERTY, like `seededFor` beside it. Leaving it global
    // meant an edit on one property marked the next one dirty too: that
    // property would seed from cache, then ignore its own fresh response and
    // save the cached values back over whatever the newer ones were — the
    // exact bug the dirty flag was added to prevent, one property over.
    if (seededFor.current !== propertyId) isDirty.current = false
    // Archiving DISCARDS an unsaved edit rather than freezing it (CAP-382).
    // The Archive card sits on this same Settings tab, so an operator can type
    // a rate, archive the property without saving, and this card re-renders
    // read-only around the draft — presenting a number that was never saved as
    // the historical figure a past payable was calculated from. That is the one
    // thing the read-only card exists to be trusted about, and there is nothing
    // to protect the edit for: the form it belonged to is gone.
    if (archived) isDirty.current = false
    if (seededFor.current === propertyId && isDirty.current) return
    setEnabled(data.settings.enabled)
    setDefaultTeamId(data.settings.default_team_id ?? '')
    setRates(
      Object.fromEntries(
        data.teams.map(team => {
          const rate = data.settings.rates[team.id]
          return [team.id, typeof rate === 'number' ? String(rate) : '']
        })
      )
    )
    seededFor.current = propertyId
  }, [data, propertyId, archived])

  // Every control routes its change through this, so "has the operator touched
  // the form" is one fact rather than one per field.
  const markDirty = () => {
    isDirty.current = true
  }

  const teams = data?.teams ?? []
  /**
   * Which crews get a row.
   *
   * A retired crew still appears when it is this property's current default or
   * carries a rate here, so an existing arrangement never silently disappears
   * from the screen that is supposed to explain it.
   *
   * Computed from the LOADED arrangement, never from the draft. Keying it on
   * the draft rate meant that clearing a retired crew's box to retype it
   * unmounted the row under the cursor: the operator could not finish typing,
   * could not undo, and saving then dropped the rate. Visibility is a fact
   * about what this property is set up with, not about what is currently in an
   * input.
   */
  const loadedRates = data?.settings.rates ?? {}
  const loadedDefaultTeamId = data?.settings.default_team_id
  const visibleTeams = teams.filter(
    team =>
      team.active ||
      team.id === loadedDefaultTeamId ||
      team.id === defaultTeamId ||
      loadedRates[team.id] !== undefined
  )

  /**
   * Being VISIBLE and being ASSIGNABLE are different questions, and conflating
   * them let a retired crew be picked as a new default purely because it still
   * had an old rate here. Retiring a crew means exactly one thing: it cannot be
   * newly assigned. It stays visible so its history stays explicable, and it
   * stays selectable only while it is already this property's default, so that
   * an existing arrangement is not silently changed just by opening the form.
   */
  const canAssign = (team: CleaningTeam) =>
    team.active || team.id === loadedDefaultTeamId || team.id === defaultTeamId

  const parsedRates = (): Record<string, number> => {
    const out: Record<string, number> = {}
    for (const [teamId, raw] of Object.entries(rates)) {
      const trimmed = raw.trim()
      if (trimmed === '') continue
      const value = Number(trimmed)
      if (Number.isFinite(value) && value >= 0) out[teamId] = value
    }
    return out
  }

  const invalidRate = Object.values(rates).some(raw => {
    const trimmed = raw.trim()
    if (trimmed === '') return false
    const value = Number(trimmed)
    return !Number.isFinite(value) || value < 0
  })

  const effectiveRates = parsedRates()
  const missingDefaultTeam = enabled && !defaultTeamId
  const missingDefaultRate =
    enabled && !!defaultTeamId && effectiveRates[defaultTeamId] === undefined

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await api.updatePropertyCleaning(propertyId, {
        enabled,
        default_team_id: defaultTeamId === '' ? null : defaultTeamId,
        // Sent whole: the API replaces the map rather than merging, which is
        // what makes clearing a rate actually clear it.
        rates: effectiveRates,
      })
      // Cleared BEFORE the refetch lands, so the invalidated query's fresh
      // response re-seeds the form rather than being ignored as an edit.
      isDirty.current = false
      await queryClient.invalidateQueries({ queryKey: ['property-cleaning', propertyId] })
      toast({
        title: 'Cleaning settings saved',
        description: enabled
          ? 'This property will get cleaning jobs.'
          : 'This property is excluded from cleaning.',
      })
    } catch (err: unknown) {
      toast({
        title: 'Failed to save cleaning settings',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cleaning</CardTitle>
        <CardDescription>
          Whether this property gets cleaning jobs, which crew takes it by default, and what each
          crew charges here. Crews and the global check-in/check-out times are set on Settings.
        </CardDescription>
        {/* Said plainly rather than left to an inert form: an offboarded
            property's cleanings did happen and were paid for, and the rates
            below are what those amounts came from. */}
        {archived && (
          <p className="mt-2 rounded-card border border-border bg-surface2 p-3 text-sm text-textMuted">
            This property is no longer managed
            {managedUntil ? ` as of ${managedUntil}` : ''}, so no new cleanings are being created
            for it. Its past cleanings and what each was paid are kept, and the rates below are what
            those amounts were calculated from.
          </p>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton className="h-32 w-full" />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <div className="space-y-4">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                disabled={isSaving || archived}
                onChange={e => {
                  markDirty()
                  setEnabled(e.target.checked)
                }}
                className="mt-0.5 h-4 w-4 rounded border-border text-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
              />
              <span>
                <span className="font-medium text-text">Cleaning enabled</span>
                <span className="block text-xs text-textMuted">
                  Off means no cleaning jobs are created for this property at all.
                </span>
              </span>
            </label>

            <div className="space-y-1">
              <label
                htmlFor={`cleaning-default-team-${propertyId}`}
                className="text-xs font-medium text-textMuted"
              >
                Default crew
              </label>
              <select
                id={`cleaning-default-team-${propertyId}`}
                value={defaultTeamId}
                disabled={isSaving || archived || !enabled}
                onChange={e => {
                  markDirty()
                  setDefaultTeamId(e.target.value)
                }}
                className="flex h-8 w-full max-w-xs rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">No default crew</option>
                {visibleTeams.map(team => (
                  <option key={team.id} value={team.id} disabled={!canAssign(team)}>
                    {team.name}
                    {team.active ? '' : ' (retired)'}
                  </option>
                ))}
              </select>
              {missingDefaultTeam && (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  Cleaning is on for this property but no crew is set, so its jobs will be created
                  with nobody assigned.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-textMuted">Rates for this property</p>
              {visibleTeams.length === 0 ? (
                <p className="text-xs text-textMuted">
                  No crews exist yet. Add one on Settings first.
                </p>
              ) : (
                <div className="space-y-2">
                  {visibleTeams.map(team => (
                    <div key={team.id} className="flex flex-wrap items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="h-3 w-3 shrink-0 rounded-full border border-border"
                        style={{ backgroundColor: team.color }}
                      />
                      <label
                        htmlFor={`cleaning-rate-${propertyId}-${team.id}`}
                        className="min-w-0 flex-1 text-sm text-text"
                      >
                        {team.short_name}
                        {team.id === defaultTeamId && (
                          <Badge variant="secondary" className="ml-2 align-middle">
                            Default
                          </Badge>
                        )}
                      </label>
                      <span className="text-sm text-textMuted">$</span>
                      <input
                        id={`cleaning-rate-${propertyId}-${team.id}`}
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        placeholder="not set"
                        value={rates[team.id] ?? ''}
                        disabled={isSaving || archived}
                        onChange={e => {
                          markDirty()
                          setRates(prev => ({ ...prev, [team.id]: e.target.value }))
                        }}
                        className="flex h-8 w-24 rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-textMuted">
                Leave a rate blank if that crew has no agreed price here. Blank is not the same as
                $0 - a blank rate means a payable cannot be raised for that crew.
              </p>
              {missingDefaultRate && (
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  The default crew has no rate for this property, so its cleanings cannot be paid
                  out.
                </p>
              )}
              {invalidRate && (
                <p className="text-xs text-red-600 dark:text-red-500">
                  Rates must be zero or more.
                </p>
              )}
            </div>

            {!archived && (
              <Button
                type="button"
                size="sm"
                disabled={isSaving || invalidRate}
                onClick={handleSave}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
